import { FC } from "react";
import { Pressable } from "react-native";
import {
  CheckIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  LockIcon,
} from "lucide-react-native";

import { VnBadge } from "@/components/VnBadge/VnBadge";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnIcon } from "@/components/VnIcon";
import { VnProgress } from "@/components/VnProgress";
import { VnText } from "@/components/VnText/VnText";
import {
  getExemptionI18nKey,
  WorkRequirementMemberDetailType,
  WorkRequirementSlotType,
  WorkRequirementType,
} from "@/domain/workRequirements";
import { useTranslator } from "@/hooks/useTranslate";
import { I18nKeyType } from "@/i18n";
import { dateAsDayjs } from "@/utils/date";
import { formatHouseholdMemberName } from "@/utils/householdMember";
import { cn } from "@/utils/tailwind";

type WorkRequirementMemberCardPropsType = {
  member: WorkRequirementMemberDetailType;
  rule: WorkRequirementType["rule"];
  onSlotPress: (slot: WorkRequirementSlotType) => void;
  onVolunteerOpportunitiesPress: () => void;
};

const SlotRow: FC<{
  slot: WorkRequirementSlotType;
  // Rows are divided from each other, not from the progress bar above.
  hasDivider: boolean;
  onPress: () => void;
}> = ({ slot, hasDivider, onPress }) => {
  const titleI18nKey =
    slot.kind === "PROOF_OF_WORK"
      ? "native:workRequirements.nextSteps.proofOfWork"
      : "native:workRequirements.nextSteps.proofOfExemption";
  const month = slot.coverageMonth
    ? dateAsDayjs(slot.coverageMonth).format("MMM YYYY")
    : undefined;
  const isComplete = slot.documents.length > 0;

  if (slot.isLocked && slot.coverageMonth) {
    return (
      <VnBox
        className={cn(
          "border-border flex-row items-center justify-between gap-3 py-4",
          hasDivider && "border-t",
        )}>
        <VnText
          variant="lg"
          className="text-muted-foreground flex-1"
          i18nKey={titleI18nKey}
          i18nOptions={{ month }}
        />
        <VnText
          variant="md"
          className="text-muted-foreground"
          i18nKey="native:workRequirements.nextSteps.opensOn"
          i18nOptions={{
            date: dateAsDayjs(slot.coverageMonth).format("MMM D"),
          }}
        />
        <VnIcon as={LockIcon} size={18} className="text-muted-foreground" />
      </VnBox>
    );
  }

  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      className={cn(
        "border-border flex-row items-center justify-between gap-3 py-4",
        hasDivider && "border-t",
      )}>
      {isComplete && (
        <VnBox className="bg-border h-6 w-6 flex-shrink-0 items-center justify-center rounded-full">
          <VnIcon as={CheckIcon} />
        </VnBox>
      )}
      <VnText
        variant="lg"
        className="flex-1"
        i18nKey={titleI18nKey}
        i18nOptions={{ month }}
      />
      <VnIcon as={ChevronRightIcon} />
    </Pressable>
  );
};

export const WorkRequirementMemberCard: FC<
  WorkRequirementMemberCardPropsType
> = ({ member, rule, onSlotPress, onVolunteerOpportunitiesPress }) => {
  const { t } = useTranslator();
  const isOffTrack = member.status === "DOES_NOT_MEET_REQUIREMENTS";

  const reason = (() => {
    switch (member.status) {
      case "EXEMPT":
        return member.exemptions
          .map(exemption => t(getExemptionI18nKey(exemption)))
          .join(t("common:listSeparator"));
      case "MEETS_REQUIREMENTS":
        return member.hasQualifyingIncome
          ? t("native:workRequirements.nextSteps.reasonIncome", {
              amount: rule.monthlyIncomeThreshold,
            })
          : t("native:workRequirements.nextSteps.reasonHours", {
              hours: member.totalHours,
            });
      default:
        return t("native:workRequirements.nextSteps.reportedOf", {
          hours: member.totalHours,
          required: rule.requiredMonthlyHours,
        });
    }
  })();

  const statusI18nKey: I18nKeyType = isOffTrack
    ? "native:workRequirements.nextSteps.needsMoreHours"
    : member.status === "EXEMPT"
      ? "native:workRequirements.nextSteps.status_EXEMPT"
      : "native:workRequirements.nextSteps.status_MEETS_REQUIREMENTS";

  const missingHours =
    Math.round((rule.requiredMonthlyHours - member.totalHours) * 10) / 10;

  return (
    <VnCard className="gap-0 p-0">
      <VnBox className="gap-2 p-5">
        <VnBox className="flex-row items-start justify-between gap-3">
          <VnText variant="lgMedium" className="flex-1">
            {formatHouseholdMemberName(member)}
          </VnText>
          <VnBadge
            variant="outline"
            className={cn(
              "rounded-md",
              isOffTrack
                ? "border-destructive bg-destructive/10"
                : "border-chart-five bg-chart-five/10",
            )}>
            <VnText
              variant="smMedium"
              className={isOffTrack ? "text-destructive" : "text-foreground"}
              i18nKey={statusI18nKey}
              i18nOptions={{ hours: missingHours }}
            />
          </VnBadge>
        </VnBox>
        <VnText variant="md" className="text-muted-foreground">
          {reason}
        </VnText>
      </VnBox>

      {member.slots.length > 0 && (
        <VnBox className="border-border border-t px-5 pt-4">
          <VnText
            variant="sm"
            className="text-muted-foreground"
            i18nKey="native:workRequirements.nextSteps.complete"
            i18nOptions={{
              completed: member.completedDocumentCount,
              total: member.requiredDocumentCount,
            }}
          />
          <VnProgress
            value={
              (member.completedDocumentCount / member.requiredDocumentCount) *
              100
            }
            className="mt-2"
          />
          {member.slots.map((slot, index) => (
            <SlotRow
              key={slot.coverageMonth ?? slot.kind}
              slot={slot}
              hasDivider={index > 0}
              onPress={() => onSlotPress(slot)}
            />
          ))}
        </VnBox>
      )}

      {isOffTrack && (
        <VnBox className="border-border gap-2 border-t p-5">
          <VnText
            variant="md"
            className="text-muted-foreground"
            i18nKey="native:workRequirements.nextSteps.offrampDescription"
            i18nOptions={{ hours: rule.requiredMonthlyHours }}
          />
          <VnButton
            variant="link"
            onPress={onVolunteerOpportunitiesPress}
            IconComponent={ExternalLinkIcon}
            iconPosition="right"
            className="self-start has-[>svg]:px-0"
            i18nKey="native:workRequirements.nextSteps.volunteerOpportunities"
          />
        </VnBox>
      )}
    </VnCard>
  );
};
